import React from 'react'
import i18n from 'live/services/i18n'
// import OptionList from 'live/components/lists/ui/option-list'
import OptionList from './option-list'
import styled from 'styled-components'
import _ from 'lodash'
import Select from 'react-select'

type Props = {
    className?: string
    group: GridEditorGroup
    channels: Array<LiveSelectChannelOption>
    selectedChannels: Array<LiveSelectChannelOption>
    onChangeGroupChannels: (newChannels: Array<GridEditorChannelOption>, updateQuery: boolean) => void
    renderEntireSelectedOption: ({
        option,
        onRemove
    }: {
        option: LiveSelectChannelOption
        onRemove: () => void
    }) => JSX.Element
    loadingGroups: boolean
    loadingChannels: boolean
    disabled?: boolean
    renderSelectedOption?: (LiveSelectOption: LiveSelectOption) => JSX.Element
    placeholder?: string
    creatable?: boolean
    onChangeGroup: (group: GridEditorGroup, updateQuery: boolean) => void
}

const MAX_CHANNELS = 50

export default styled(
    class ChannelSelector extends React.Component<Props> {
        onSelectChannel = (channel: LiveSelectChannelOption): void => {
            if ((this.props.group ?? {}).channels.find(c => c.mnemonic === channel.mnemonic)) return
            this.props.onChangeGroupChannels(
                [
                    ...(this.props?.group?.channels ?? []),
                    {
                        ...channel,
                        __icon: 'check',
                        iconColor: '',
                        backgroundColor: '',
                        showAlarmConfigs: false,
                        alarms: {
                            configured: {
                                background: '',
                                iconColor: 'green',
                                icon: 'check'
                            },
                            warning: {
                                background: '#E6E600',
                                iconColor: '#E6E600',
                                icon: 'exclamation',
                                blink: 1000
                            },
                            critical: {
                                background: '#CC2900',
                                iconColor: '#CC2900',
                                icon: 'exclamation',
                                blink: 1000
                            },
                            notMapped: {
                                background: 'blue',
                                iconColor: 'blue',
                                icon: 'times'
                            }
                        }
                    }
                ],
                true
            )
        }

        onRemoveChannel = (channel: LiveSelectChannelOption): void => {
            const newGroupChannels: Array<GridEditorChannelOption> = (this.props.group ?? {}).channels.filter(
                c => c.mnemonic !== channel.mnemonic
            )
            this.props.onChangeGroupChannels(newGroupChannels, true)
        }

        renderSelectedOption = (option: LiveSelectChannelOption): JSX.Element => {
            return <SelectedChannel name={option.label} {...option.data} />
        }

        keyExtractor = (option: LiveSelectChannelOption): string => _.uniqueId(option.value)

        loadChannels = _.debounce((input, callback) => {
            const value = input.toLocaleLowerCase()

            const channels = this.props.channels
                .filter(c => c.label.toLowerCase().indexOf(value) !== -1 || c.value.toLowerCase().indexOf(value) !== -1)
                .slice(0, MAX_CHANNELS)

            callback(null, {
                options: channels,
                complete: channels.length <= MAX_CHANNELS
            })
        }, 300)

        renderSelect = (): JSX.Element => {
            return (
                <div data-testid={`${this.props.group}-channel-selector`} className="form-group">
                    <Select.Async
                        className="channel-select option-list__input dropdown-toggler--cancel-drag"
                        groupClassName="option-list__input-container"
                        loadOptions={this.loadChannels}
                        disabled={this.props.disabled}
                        placeholder={this.props.placeholder}
                        onChange={this.onSelectChannel}
                        creatable={this.props.creatable}
                    />
                </div>
            )
        }

        render(): JSX.Element {
            const {
                group,
                className,
                loadingChannels,
                disabled,
                loadingGroups,
                selectedChannels,
                renderSelectedOption,
                renderEntireSelectedOption
            } = this.props
            const groupID = _.get(group, 'value')

            return (
                <OptionList
                    className={className}
                    disabled={groupID === undefined || loadingChannels || disabled}
                    placeholder={
                        loadingGroups
                            ? i18n('Loading...')
                            : !groupID
                            ? i18n('Select a data first')
                            : loadingChannels
                            ? i18n('Loading channels..')
                            : i18n('Add a channel')
                    }
                    selectedOptions={[...selectedChannels]}
                    renderSelectedOption={renderSelectedOption || this.renderSelectedOption}
                    renderSelect={this.renderSelect}
                    renderEntireSelectedOption={renderEntireSelectedOption}
                    keyExtractor={this.keyExtractor}
                    onRemove={this.onRemoveChannel}
                    onChangeGroupChannels={this.props.onChangeGroupChannels}
                />
            )
        }
    }
)``

type SelectedChannelProps = {
    name: string
    unit?: string
    color?: string
}

const SelectedChannel = ({ name, unit, color }: SelectedChannelProps): JSX.Element => {
    return (
        <SelectedChannelContainer>
            <ChannelName>
                {`${name} `}
                {unit ? `(${unit})` : null}
            </ChannelName>
            <ColorLabel className={`channels-selector__color-label`} color={color} />
        </SelectedChannelContainer>
    )
}

const SelectedChannelContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    z-index: 1000;
`

const ChannelName = styled.span`
    max-width: 130px;
    overflow: hidden;
    text-overflow: ellipsis;
`

const ColorLabel = styled.div`
    background-color: ${props => props.color};
    width: 18px;
    height: 18px;
    margin-left: auto;
    border: ${props => (props.color ? 'solid 1px #f8f8f8' : 'none')};
`
